import { useRef, useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Camera, Loader2, X } from "lucide-react";
import { fileUrl, api } from "../lib/api";

const initials = (name) =>
  (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const SIZES = { sm: "h-9 w-9", md: "h-12 w-12", lg: "h-20 w-20" };

export const EmployeeAvatar = ({ employee, size = "sm", editable = false, onUploaded }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [photoId, setPhotoId] = useState(employee.photo_id || null);
  // cache-bust so a newly-uploaded photo shows immediately
  const currentPhoto = photoId || employee.photo_id;
  const src = currentPhoto ? `${fileUrl(currentPhoto)}?v=${currentPhoto}` : null;

  const pick = (e) => {
    e.stopPropagation();
    inputRef.current?.click();
  };

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadEmployeePhoto(employee.id, file);
      setPhotoId(res.photo_id);
      toast.success("تم تحديث صورة الموظف");
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر رفع الصورة");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.deleteEmployeePhoto(employee.id);
      setPhotoId(null);
      toast.success("تمت إزالة صورة الموظف");
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر حذف الصورة");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-flex shrink-0" data-testid={`employee-avatar-${employee.id}`}>
      <Avatar className={SIZES[size]}>
        {src && <AvatarImage src={src} alt={employee.name} className="object-cover" />}
        <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold text-sm">
          {initials(employee.name)}
        </AvatarFallback>
      </Avatar>
      {editable && (
        <>
          <button type="button" onClick={pick} disabled={busy}
            data-testid={`employee-photo-button-${employee.id}`}
            className="absolute -bottom-1 -left-1 h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center shadow ring-2 ring-white hover:bg-blue-700 transition disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
          </button>
          {currentPhoto && !busy && (
            <button type="button" onClick={removePhoto}
              data-testid={`employee-photo-remove-${employee.id}`}
              className="absolute -top-1 -left-1 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center shadow ring-2 ring-white hover:bg-red-600 transition">
              <X className="h-2.5 w-2.5" />
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid={`employee-photo-input-${employee.id}`} onChange={onChange} />
        </>
      )}
    </div>
  );
};
